import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Snackbar,
  Chip,
  Stack,
  Checkbox,
  MenuItem,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useApi } from "../../hooks/useApi";

interface OrderInsightInfo {
  id: number;
  verifiMerchantCleanName: string;
  storeName: string;
  merchantName: string;
  merchantUrl: string;
  merchantContactPhone: string;
}

interface MerchantWithInfo {
  cleanName: string;
  fullNames: string[];
  info: OrderInsightInfo | null;
}

interface FormState {
  verifiMerchantCleanName: string;
  storeName: string;
  merchantName: string;
  merchantUrl: string;
  merchantContactPhone: string;
}

const EMPTY_FORM: FormState = {
  verifiMerchantCleanName: "",
  storeName: "",
  merchantName: "",
  merchantUrl: "",
  merchantContactPhone: "",
};

interface Props {
  companyId: string;
}

export default function OrderInsightMerchantInfoManager({ companyId }: Props) {
  const { get, post, put, del, loading } = useApi();

  const [merchants, setMerchants] = useState<MerchantWithInfo[]>([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInfoId, setEditingInfoId] = useState<number | null>(null);
  const [bulkTargets, setBulkTargets] = useState<string[] | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [copyFromCleanName, setCopyFromCleanName] = useState("");
  const [apiError, setApiError] = useState<string | null>(null);

  const [selectedCleanNames, setSelectedCleanNames] = useState<Set<string>>(new Set());

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingInfo, setDeletingInfo] = useState<{ id: number; cleanName: string } | null>(null);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const baseUrl = `/api/companies/${companyId}/order-insight-merchant-info`;

  const fetchAll = useCallback(async () => {
    try {
      const data = await get<MerchantWithInfo[]>(baseUrl);
      setMerchants(data);
      const unconfigured = new Set(data.filter(m => m.info === null).map(m => m.cleanName));
      setSelectedCleanNames(prev => new Set([...prev].filter(cn => unconfigured.has(cn))));
    } catch (err) {
      console.error("Failed to fetch order insight merchant info:", err);
    }
  }, [get, baseUrl]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // --- Dialog handlers ---

  const openAdd = (cleanName: string) => {
    setEditingInfoId(null);
    setBulkTargets(null);
    setForm({ ...EMPTY_FORM, verifiMerchantCleanName: cleanName });
    setCopyFromCleanName("");
    setApiError(null);
    setDialogOpen(true);
  };

  const openEdit = (info: OrderInsightInfo) => {
    setEditingInfoId(info.id);
    setBulkTargets(null);
    setForm({
      verifiMerchantCleanName: info.verifiMerchantCleanName,
      storeName: info.storeName,
      merchantName: info.merchantName,
      merchantUrl: info.merchantUrl,
      merchantContactPhone: info.merchantContactPhone,
    });
    setCopyFromCleanName("");
    setApiError(null);
    setDialogOpen(true);
  };

  const openBulk = () => {
    setEditingInfoId(null);
    setBulkTargets([...selectedCleanNames]);
    setForm(EMPTY_FORM);
    setCopyFromCleanName("");
    setApiError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingInfoId(null);
    setBulkTargets(null);
    setForm(EMPTY_FORM);
    setCopyFromCleanName("");
    setApiError(null);
  };

  const onDialogClose = (_event: object, reason: string) => {
    if (reason === "backdropClick") return;
    closeDialog();
  };

  const openDelete = (info: OrderInsightInfo) => {
    setDeletingInfo({ id: info.id, cleanName: info.verifiMerchantCleanName });
    setApiError(null);
    setDeleteDialogOpen(true);
  };

  const closeDelete = () => {
    setDeleteDialogOpen(false);
    setDeletingInfo(null);
    setApiError(null);
  };

  // --- Selection ---

  const selectableMerchants = merchants.filter(m => m.info === null);
  const allSelected = selectableMerchants.length > 0 && selectableMerchants.every(m => selectedCleanNames.has(m.cleanName));
  const someSelected = selectableMerchants.some(m => selectedCleanNames.has(m.cleanName)) && !allSelected;

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedCleanNames(prev => {
        const next = new Set(prev);
        selectableMerchants.forEach(m => next.delete(m.cleanName));
        return next;
      });
    } else {
      setSelectedCleanNames(prev => {
        const next = new Set(prev);
        selectableMerchants.forEach(m => next.add(m.cleanName));
        return next;
      });
    }
  };

  const handleToggleSelect = (cleanName: string) => {
    setSelectedCleanNames(prev => {
      const next = new Set(prev);
      if (next.has(cleanName)) next.delete(cleanName);
      else next.add(cleanName);
      return next;
    });
  };

  const clearSelection = () => setSelectedCleanNames(new Set());

  // --- CRUD ---

  const submitBulk = async (payload: Omit<FormState, "verifiMerchantCleanName">, targets: string[]) => {
    const failures: { cleanName: string; message: string }[] = [];
    for (const cleanName of targets) {
      try {
        await post(baseUrl, { ...payload, verifiMerchantCleanName: cleanName });
      } catch (err: any) {
        failures.push({ cleanName, message: err.message || "An error occurred" });
      }
    }

    if (failures.length === 0) {
      setSnackbar({ open: true, message: `Order Insight info added for ${targets.length} merchant${targets.length !== 1 ? "s" : ""}`, severity: "success" });
      closeDialog();
      clearSelection();
    } else {
      setApiError(failures.map(f => `Failed for ${f.cleanName}: ${f.message}`).join("\n"));
      setBulkTargets(failures.map(f => f.cleanName));
    }
    fetchAll();
  };

  const submitForm = async () => {
    if (loading) return;
    const trimmed = {
      storeName: form.storeName.trim(),
      merchantName: form.merchantName.trim(),
      merchantUrl: form.merchantUrl.trim(),
      merchantContactPhone: form.merchantContactPhone.trim(),
    };
    if (!trimmed.storeName || !trimmed.merchantName || !trimmed.merchantUrl || !trimmed.merchantContactPhone) {
      setApiError("All fields are required");
      return;
    }

    if (bulkTargets !== null) {
      await submitBulk(trimmed, bulkTargets);
      return;
    }

    const payload: FormState = { ...trimmed, verifiMerchantCleanName: form.verifiMerchantCleanName.trim() };
    try {
      if (editingInfoId !== null) {
        await put(`${baseUrl}/${editingInfoId}`, payload);
        setSnackbar({ open: true, message: "Order Insight info updated", severity: "success" });
      } else {
        await post(baseUrl, payload);
        setSnackbar({ open: true, message: "Order Insight info added", severity: "success" });
      }
      closeDialog();
      fetchAll();
    } catch (err: any) {
      setApiError(err.message || "An error occurred");
    }
  };

  const confirmDelete = async () => {
    if (!deletingInfo || loading) return;
    try {
      await del(`${baseUrl}/${deletingInfo.id}`);
      setSnackbar({ open: true, message: "Order Insight info deleted", severity: "success" });
      closeDelete();
      fetchAll();
    } catch (err: any) {
      setApiError(err.message || "An error occurred");
    }
  };

  const updateField = (key: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement>) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  const copyFromOptions = merchants.filter(m => m.info !== null && m.cleanName !== form.verifiMerchantCleanName);

  const handleCopyFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleanName = e.target.value;
    setCopyFromCleanName(cleanName);
    const source = merchants.find(m => m.cleanName === cleanName);
    if (source?.info) {
      setForm(prev => ({
        ...prev,
        storeName: source.info!.storeName,
        merchantName: source.info!.merchantName,
        merchantUrl: source.info!.merchantUrl,
        merchantContactPhone: source.info!.merchantContactPhone,
      }));
    }
  };

  return (
    <div>
      <Box mb={2}>
        <Typography variant="body2" color="text.secondary">
          Merchants in this company and the Order Insight response info attached to each.
        </Typography>
      </Box>

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={selectedCleanNames.size > 0 ? { backgroundColor: "#EAF6FF" } : undefined}>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={someSelected}
                    checked={allSelected}
                    onChange={handleSelectAll}
                    size="small"
                  />
                </TableCell>
                {selectedCleanNames.size > 0 ? (
                  <TableCell colSpan={100}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {selectedCleanNames.size} merchant{selectedCleanNames.size !== 1 ? "s" : ""} selected
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={openBulk}
                        sx={{ backgroundColor: "#465fff", "&:hover": { backgroundColor: "#3641f5" }, textTransform: "none" }}
                      >
                        Set info for selected
                      </Button>
                      <Button size="small" color="inherit" onClick={clearSelection} sx={{ textTransform: "none" }}>
                        Clear
                      </Button>
                    </Stack>
                  </TableCell>
                ) : (
                  <>
                    <TableCell sx={{ width: "20%" }}>Group</TableCell>
                    <TableCell sx={{ width: "35%" }}>Merchant(s)</TableCell>
                    <TableCell>Order Insight details</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {merchants.map(m => (
                <TableRow key={m.cleanName} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedCleanNames.has(m.cleanName)}
                      onChange={() => handleToggleSelect(m.cleanName)}
                      disabled={m.info !== null}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ verticalAlign: "top" }}>{m.cleanName}</TableCell>
                  <TableCell sx={{ verticalAlign: "top", whiteSpace: "pre-line" }}>
                    {m.fullNames.length > 0 ? m.fullNames.join("\n") : "—"}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: "top" }}>
                    {m.info ? (
                      <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                          <Chip size="small" label={`Store: ${m.info.storeName}`} />
                          <Chip size="small" label={`Merchant: ${m.info.merchantName}`} />
                          <Chip size="small" label={`URL: ${m.info.merchantUrl}`} />
                          <Chip size="small" label={`Phone: ${m.info.merchantContactPhone}`} />
                        </Stack>
                        <Box display="flex" gap={0.5} sx={{ flexShrink: 0 }}>
                          <IconButton size="small" onClick={() => openEdit(m.info!)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => openDelete(m.info!)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => openAdd(m.cleanName)}
                      >
                        Add Info
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {merchants.length === 0 && (
          <Box className="text-center py-8">
            <Typography variant="body1" color="text.secondary">
              No merchants found for this company.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={onDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingInfoId !== null
            ? "Edit Order Insight info"
            : bulkTargets !== null
              ? `Add Order Insight info — ${bulkTargets.length} merchant${bulkTargets.length !== 1 ? "s" : ""}`
              : "Add Order Insight info"}
        </DialogTitle>
        <DialogContent>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2, whiteSpace: "pre-line" }}>{apiError}</Alert>
          )}
          {bulkTargets !== null && (
            <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 2 }}>
              {bulkTargets.map(cleanName => (
                <Chip key={cleanName} size="small" label={cleanName} />
              ))}
            </Stack>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            {copyFromOptions.length > 0 && (
              <TextField
                select
                label="Copy from"
                value={copyFromCleanName}
                onChange={handleCopyFromChange}
                fullWidth
                size="small"
                disabled={loading}
                helperText="Prefill the fields from a merchant that already has info."
              >
                {copyFromOptions.map(m => (
                  <MenuItem key={m.cleanName} value={m.cleanName}>{m.cleanName}</MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label="Store name"
              value={form.storeName}
              onChange={updateField("storeName")}
              fullWidth
              required
              size="small"
              autoFocus
              disabled={loading}
              inputProps={{ maxLength: 100 }}
              helperText="Store/Webstore name where purchase was made. Should be recognizable to the consumer (not a parent company or non-consumer facing company name)."
            />
            <TextField
              label="Merchant name"
              value={form.merchantName}
              onChange={updateField("merchantName")}
              fullWidth
              required
              size="small"
              disabled={loading}
              inputProps={{ maxLength: 100 }}
              helperText="Corporate or Parent company name of the Merchant, may or may not be recognizable to the consumer. May be the name on file with your Acquirer/Merchant Account provider."
            />
            <TextField
              label="Merchant URL"
              value={form.merchantUrl}
              onChange={updateField("merchantUrl")}
              fullWidth
              required
              size="small"
              disabled={loading}
              inputProps={{ maxLength: 1000 }}
              helperText="Merchant corporate business URL. May or may NOT be different from the URL the purchase was made from."
            />
            <TextField
              label="Merchant contact phone"
              value={form.merchantContactPhone}
              onChange={updateField("merchantContactPhone")}
              fullWidth
              required
              size="small"
              disabled={loading}
              inputProps={{ maxLength: 20 }}
              helperText={'Merchant’s customer service phone number. This should be the number you would want a consumer to contact you to discuss any questions they may have about the purchase. Must be in E.164 format (“+”, 3-digit country code, 12-digit telephone number).'}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} disabled={loading}>Cancel</Button>
          <Button
            onClick={submitForm}
            variant="contained"
            disabled={loading}
            sx={{ backgroundColor: "#465fff", "&:hover": { backgroundColor: "#3641f5" } }}
          >
            {loading
              ? "Saving..."
              : editingInfoId !== null
                ? "Update"
                : bulkTargets !== null
                  ? `Add for ${bulkTargets.length}`
                  : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDelete}>
        <DialogTitle>Delete Order Insight info</DialogTitle>
        <DialogContent>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>{apiError}</Alert>
          )}
          <Typography>
            Delete Order Insight info for <strong>{deletingInfo?.cleanName}</strong>? Order Insight responses for matching MIDs will fall back to UNCONFIGURED_ACCOUNT.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDelete} disabled={loading}>Cancel</Button>
          <Button onClick={confirmDelete} variant="contained" color="error" disabled={loading}>
            {loading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
